/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isObservable, take } from 'rxjs';
import { IDialogInput } from './../../interfaces';
import { buildResizeBounds, clampPanelSize } from './dialog-resize.utils';
import { ResizeBounds } from './dialog-resize.interfaces';
@Component({
  selector: 'firebird-web-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  standalone: false,
})
export class DialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('resizeHandle') private resizeHandle?: ElementRef<HTMLElement>;
  @ViewChild(CdkDrag) private dragRef?: CdkDrag;

  private pane: HTMLElement | null = null;
  private resizeStartDragPosition = { x: 0, y: 0 };
  private resizePointerId: number | null = null;
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private resizeBounds: ResizeBounds | null = null;
  private resizeCleanups: Array<() => void> = [];
  private readonly cleanups: Array<() => void> = [];

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: IDialogInput,
    private dialogRef: MatDialogRef<DialogComponent>,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly renderer: Renderer2
  ) {}

  public ngAfterViewInit(): void {
    const handle = this.resizeHandle?.nativeElement;

    if (!handle) {
      return;
    }

    this.pane = this.elementRef.nativeElement.closest(
      '.firebird-floating-panel'
    );

    this.zone.runOutsideAngular(() => {
      this.cleanups.push(
        this.renderer.listen(handle, 'pointerdown', (event: PointerEvent) =>
          this.onResizePointerDown(event)
        )
      );
    });
  }

  public ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.resizeCleanups.forEach((fn) => fn());
  }

  /**
   * Returns true if passed reference is of type TemplateRef
   */
  public isTemplate(reference: any): boolean {
    return reference instanceof TemplateRef;
  }
  public onManageClick(): void {
    this.dialogRef.close('Manage');
  }

  public close(): void {
    const closeGuardResult = this.data.beforeClose?.();

    if (isObservable(closeGuardResult)) {
      closeGuardResult
        .pipe(take(1))
        .subscribe((shouldClose) => this.closeIfAllowed(shouldClose));
      return;
    }

    this.closeIfAllowed(closeGuardResult ?? true);
  }

  private closeIfAllowed(shouldClose: boolean): void {
    if (!shouldClose) {
      return;
    }

    this.dialogRef.close({ saved: false });
  }

  private onResizePointerDown(event: PointerEvent): void {
    const handle = this.resizeHandle?.nativeElement;

    if (!handle || !this.pane) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const rect = this.pane.getBoundingClientRect();
    this.resizePointerId = event.pointerId;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = rect.width;
    this.resizeStartHeight = rect.height;
    // MatDialog flex-centres the pane, so resizing moves all four edges; the
    // move handler counter-shifts by half the delta to pin the top-left.
    this.resizeStartDragPosition = this.dragRef?.getFreeDragPosition() ?? {
      x: 0,
      y: 0,
    };
    this.resizeBounds = buildResizeBounds(
      rect.left,
      rect.top,
      window.innerWidth,
      window.innerHeight
    );
    handle.setPointerCapture(event.pointerId);

    const end = () => this.endResize();
    this.resizeCleanups = [
      this.renderer.listen(handle, 'pointermove', (move: PointerEvent) =>
        this.onResizePointerMove(move)
      ),
      this.renderer.listen(handle, 'pointerup', end),
      this.renderer.listen(handle, 'pointercancel', end),
      this.renderer.listen(handle, 'lostpointercapture', end),
    ];
  }

  private onResizePointerMove(event: PointerEvent): void {
    if (this.resizePointerId === null || !this.pane || !this.resizeBounds) {
      return;
    }

    const size = clampPanelSize(
      {
        width: this.resizeStartWidth + (event.clientX - this.resizeStartX),
        height: this.resizeStartHeight + (event.clientY - this.resizeStartY),
      },
      this.resizeBounds
    );

    this.renderer.setStyle(this.pane, 'min-width', '0');
    this.renderer.setStyle(this.pane, 'min-height', '0');
    this.renderer.setStyle(this.pane, 'max-width', 'none');
    this.renderer.setStyle(this.pane, 'max-height', 'none');
    this.renderer.setStyle(this.pane, 'width', `${size.width}px`);
    this.renderer.setStyle(this.pane, 'height', `${size.height}px`);

    this.dragRef?.setFreeDragPosition({
      x:
        this.resizeStartDragPosition.x +
        (size.width - this.resizeStartWidth) / 2,
      y:
        this.resizeStartDragPosition.y +
        (size.height - this.resizeStartHeight) / 2,
    });
  }

  private endResize(): void {
    this.resizeCleanups.forEach((fn) => fn());
    this.resizeCleanups = [];
    this.resizePointerId = null;
    this.resizeBounds = null;
  }
}
